import { subscribeToNavigation } from "../shopper-intent/types";

export interface PricingTestDisplayConfig {
  variantPrices: Map<string, number>;
  productToFirstVariant: Map<string, string>;
  currency: string;
}

export interface PricingTestDisplay {
  cleanup: () => void;
}

const PRICE_SELECTOR = "[data-product-price]";
const COMPARE_PRICE_SELECTOR = "[data-product-compare-price]";
const PRODUCT_CARD_SELECTOR = "[data-product-id]";

export function setupPricingTestDisplay(
  config: PricingTestDisplayConfig,
): PricingTestDisplay {
  let observer: MutationObserver | null = null;

  const detectCurrentVariantId = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get("variant");
    if (variantParam) {
      return variantParam;
    }

    const shopifyProduct = (globalThis as any).ShopifyAnalytics?.meta?.product;
    if (shopifyProduct?.variants?.length === 1) {
      return String(shopifyProduct.variants[0].id);
    }

    const selectedVariant = (globalThis as any).ShopifyAnalytics?.meta
      ?.selectedVariantId;
    if (selectedVariant) {
      return String(selectedVariant);
    }

    return null;
  };

  const formatPrice = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: config.currency,
    }).format(amount);
  };

  const parsePrice = (text: string): number | null => {
    const cleaned = text.replace(/[^0-9.,]/g, "");
    const normalized = cleaned.replace(",", ".");
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
  };

  const replacePriceInElement = (
    element: Element,
    newPrice: number,
  ): boolean => {
    const formattedPrice = formatPrice(newPrice);
    const currentText = element.textContent?.trim();

    if (!currentText) {
      return false;
    }

    const originalPrice = parsePrice(currentText);

    const hasCurrencySuffix = currentText.endsWith(` ${config.currency}`);
    const targetText = hasCurrencySuffix
      ? `${formattedPrice} ${config.currency}`
      : formattedPrice;

    if (currentText !== targetText) {
      element.textContent = targetText;
      if (originalPrice) {
        updateComparePriceInContainer(element, originalPrice, newPrice);
      }
      return true;
    }
    return false;
  };

  const updateComparePriceInContainer = (
    priceElement: Element,
    originalSalePrice: number,
    newSalePrice: number,
  ): void => {
    const parent = priceElement.parentElement;
    if (!parent) {
      return;
    }

    for (const compareElement of parent.querySelectorAll(
      COMPARE_PRICE_SELECTOR,
    )) {
      const compareText = compareElement.textContent?.trim();
      if (!compareText) {
        continue;
      }

      const originalComparePrice = parsePrice(compareText);
      if (!originalComparePrice || originalComparePrice <= originalSalePrice) {
        continue;
      }

      const discountRatio = originalComparePrice / originalSalePrice;
      const newComparePrice = newSalePrice * discountRatio;

      const formattedNewCompare = formatPrice(newComparePrice);
      const hasCurrencySuffix = compareText.endsWith(` ${config.currency}`);
      const targetText = hasCurrencySuffix
        ? `${formattedNewCompare} ${config.currency}`
        : formattedNewCompare;

      compareElement.textContent = targetText;
    }
  };

  const extractVariantIdFromCard = (card: Element): string | null => {
    const link = card.querySelector(
      "a[href*='/products/']",
    ) as HTMLAnchorElement;
    if (!link?.href) {
      return null;
    }

    const url = new URL(link.href);
    const variantParam = url.searchParams.get("variant");
    if (variantParam) {
      return variantParam;
    }

    const match = link.href.match(/\/products\/([^/?#]+)/);
    if (match && match[1]) {
      const productHandle = match[1];
      const firstVariantId = config.productToFirstVariant.get(productHandle);
      if (firstVariantId) {
        return firstVariantId;
      }
    }

    return null;
  };

  const updateCollectionPrices = (pdpPriceElements: Set<Element>): void => {
    const processedPriceElements = new Set<Element>(pdpPriceElements);

    for (const card of document.querySelectorAll(PRODUCT_CARD_SELECTOR)) {
      const variantId = extractVariantIdFromCard(card);
      if (!variantId) {
        continue;
      }

      const testPrice = config.variantPrices.get(variantId);
      if (testPrice === undefined) {
        continue;
      }

      for (const element of card.querySelectorAll(PRICE_SELECTOR)) {
        if (processedPriceElements.has(element)) {
          continue;
        }

        replacePriceInElement(element, testPrice);
        processedPriceElements.add(element);
      }
    }
  };

  const collectPdpPriceElements = (): Set<Element> => {
    const elements = new Set<Element>();
    if (!window.location.pathname.includes("/products/")) {
      return elements;
    }

    const cartForm = document.querySelector('form[action*="/cart/add"]');
    const mainProductSection = cartForm?.closest(".shopify-section");
    if (!mainProductSection) {
      return elements;
    }

    const currentProductId = (globalThis as any).ShopifyAnalytics?.meta?.product
      ?.id;

    for (const element of mainProductSection.querySelectorAll(PRICE_SELECTOR)) {
      if (currentProductId) {
        const productContainer = element.closest(PRODUCT_CARD_SELECTOR);
        if (
          productContainer &&
          String(productContainer.getAttribute("data-product-id")) !==
            String(currentProductId)
        ) {
          continue;
        }
      }
      elements.add(element);
    }
    return elements;
  };

  const updatePdpPrice = (pdpPriceElements: Set<Element>): void => {
    const variantId = detectCurrentVariantId();
    if (!variantId) {
      return;
    }

    const testPrice = config.variantPrices.get(variantId);
    if (testPrice === undefined) {
      return;
    }

    for (const element of pdpPriceElements) {
      replacePriceInElement(element, testPrice);
    }
  };

  const updatePriceDisplay = (): void => {
    const pdpPriceElements = collectPdpPriceElements();
    updatePdpPrice(pdpPriceElements);
    updateCollectionPrices(pdpPriceElements);
  };

  const setupObserver = (): void => {
    observer = new MutationObserver(() => {
      updatePriceDisplay();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  };

  const handleNavigation = (): void => {
    setTimeout(updatePriceDisplay, 100);
  };

  const initializeWithRetry = (): void => {
    updatePriceDisplay();

    setTimeout(() => {
      updatePriceDisplay();
    }, 50);

    setTimeout(() => {
      updatePriceDisplay();
    }, 150);
  };

  initializeWithRetry();
  setupObserver();

  const unsubscribeNavigation = subscribeToNavigation(handleNavigation);

  window.addEventListener("popstate", handleNavigation);

  return {
    cleanup: () => {
      observer?.disconnect();
      unsubscribeNavigation();
      window.removeEventListener("popstate", handleNavigation);
    },
  };
}
